/**
 * /payroll-l2/burden — Employer burden breakdown with variance analysis.
 * Shows burden components, rates, and per-pay-run variance (estimated vs actual).
 */
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Alert,
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";

import { api } from "@/lib/api";
import type { BurdenComponent, BurdenVarianceRow } from "@/lib/types";
import { HelpHint } from "@/components/HelpHint";

export const Route = createFileRoute("/_auth/payroll-l2/burden")({
  component: BurdenPage,
});

function fmtMoney(v: string | null) {
  if (!v) return "—";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(Number(v));
}

function VarianceCell({ variance }: { variance: string }) {
  const n = Number(variance);
  const color = n > 0 ? "text-red-600" : n < 0 ? "text-emerald-600" : "text-slate-500";
  const Icon = n > 0 ? TrendingUpIcon : n < 0 ? TrendingDownIcon : null;
  return (
    <span className={`flex items-center gap-1 ${color}`}>
      {Icon && <Icon fontSize="small" />}
      {fmtMoney(variance)}
    </span>
  );
}

function BurdenPage() {
  const [payRunId, setPayRunId] = useState<string>("");

  const { data: components, isLoading: compLoading } = useQuery({
    queryKey: ["payroll-l2", "burden-components"],
    queryFn: async () => {
      return await api<BurdenComponent[]>("/api/payroll-l2/burden-components");
    },
  });

  const { data: payRuns } = useQuery({
    queryKey: ["payroll-l2", "pay-runs-list"],
    queryFn: async () => {
      return await api<{ id: string; period_start: string; period_end: string }[]>(
        "/api/payroll/pay-runs?limit=20",
      );
    },
  });

  const { data: variance, isLoading: varLoading } = useQuery({
    queryKey: ["payroll-l2", "burden-variance", payRunId],
    queryFn: async () => {
      return await api<BurdenVarianceRow[]>(
        `/api/payroll-l2/pay-runs/${payRunId}/burden/variance`,
      );
    },
    enabled: !!payRunId,
  });

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
        <Typography variant="h5" fontWeight={600}>Burden</Typography>
        <HelpHint docPath="payroll-l2/burden.md" label="Burden engine help" />
      </Stack>

      {/* Burden components table */}
      <Typography variant="subtitle1" fontWeight={500} mb={1}>Components</Typography>
      {compLoading && <CircularProgress size={24} />}
      {components && (
        <Table size="small" sx={{ mb: 4 }}>
          <TableHead>
            <TableRow>
              <TableCell>Code</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Basis</TableCell>
              <TableCell>Default Rate</TableCell>
              <TableCell>Active</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {components.map((c) => (
              <TableRow key={c.id} hover>
                <TableCell sx={{ fontFamily: "monospace" }}>{c.code}</TableCell>
                <TableCell>{c.name}</TableCell>
                <TableCell>{c.basis}</TableCell>
                <TableCell>{c.basis === "pct_gross" ? `${(Number(c.default_rate) * 100).toFixed(2)}%` : fmtMoney(c.default_rate)}</TableCell>
                <TableCell>{c.is_active ? "Yes" : "No"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {/* Variance analysis */}
      <Typography variant="subtitle1" fontWeight={500} mb={1}>Variance Analysis</Typography>
      <Stack direction="row" alignItems="center" gap={2} mb={2}>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Pay Run</InputLabel>
          <Select
            value={payRunId}
            label="Pay Run"
            onChange={(e) => setPayRunId(e.target.value)}
          >
            <MenuItem value=""><em>Select a pay run</em></MenuItem>
            {payRuns?.map((r) => (
              <MenuItem key={r.id} value={r.id}>
                {r.period_start} — {r.period_end}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>

      {varLoading && <CircularProgress size={24} />}
      {variance && variance.length === 0 && (
        <Alert severity="info">No burden data found for this pay run.</Alert>
      )}
      {variance && variance.length > 0 && (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Component</TableCell>
              <TableCell>Estimated</TableCell>
              <TableCell>Actual</TableCell>
              <TableCell>Variance</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {variance.map((row) => (
              <TableRow key={row.component_code} hover>
                <TableCell sx={{ fontFamily: "monospace" }}>{row.component_code}</TableCell>
                <TableCell>{fmtMoney(row.estimated)}</TableCell>
                <TableCell>{fmtMoney(row.actual)}</TableCell>
                <TableCell><VarianceCell variance={row.variance} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Box>
  );
}
